import {
  counterSaleProductResponseSchema,
  counterSaleResponseSchema,
  type CounterSale,
  type CounterSaleOperation,
  type CounterSaleProduct,
  type SessionIdentity,
  type StandardPaymentMethod,
} from "@cosmo/contracts";
import { type FormEvent, useMemo, useState } from "react";
import { emptyFiscalDraft, FiscalRequestFields, invoicePayload, type FiscalDraft } from "./FiscalRequestFields";
import { openAuditedCashDrawer } from "./cash-drawer";
import { downloadPdf, printReceiptPdf } from "./local-print";
import { buildCounterSaleTicketPdf } from "./ticket-pdf";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

const paymentMethods: Array<{ code: StandardPaymentMethod; label: string }> = [
  { code: "01", label: "Efectivo" },
  { code: "02", label: "Cheque nominativo" },
  { code: "03", label: "Transferencia electrónica de fondos" },
  { code: "04", label: "Tarjeta de crédito" },
  { code: "28", label: "Tarjeta de débito" },
];

type CartItem = CounterSaleProduct & { quantity: number };
type PaymentDraft = { methodCode: StandardPaymentMethod; reference: string; amount: number };
type CustomerDraft = {
  names: string;
  paternalSurname: string;
  maternalSurname: string;
  birthDate: string;
  phone: string;
  email: string;
  privacyAccepted: boolean;
};

const emptyCustomer: CustomerDraft = {
  names: "", paternalSurname: "", maternalSurname: "", birthDate: "",
  phone: "", email: "", privacyAccepted: false,
};

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message || "No fue posible completar la operación.";
  } catch {
    return "No fue posible completar la operación.";
  }
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function pdfText(value: string): string {
  return value.replace(/·/g, "-").replace(/–/g, "-").replace(/—/g, "-");
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

async function ticketPdf(sale: CounterSale): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const lines: Array<{ text: string; bold?: boolean; align?: "center" | "right" }> = [
    { text: "COSMO OPTICAS", bold: true, align: "center" },
    { text: sale.operationType === "LAYAWAY_50" ? "COMPROBANTE DE APARTADO" : "COMPROBANTE DE VENTA", bold: true, align: "center" },
    { text: sale.folio, bold: true, align: "center" },
    { text: shortDate(sale.createdAt), align: "center" },
    { text: `${sale.branchName} #${sale.branchCode}`, align: "center" },
    { text: "--------------------------------" },
    { text: sale.customerName },
    { text: "--------------------------------" },
  ];
  sale.items.forEach((item) => {
    lines.push({ text: `${item.quantity} x ${item.sku} ${item.description}` });
    lines.push({ text: money.format(item.lineTotal), align: "right" });
  });
  lines.push(
    { text: "--------------------------------" },
    { text: `Subtotal: ${money.format(sale.subtotal)}`, align: "right" },
    { text: `IVA incluido: ${money.format(sale.vat)}`, align: "right" },
    { text: `TOTAL: ${money.format(sale.total)}`, bold: true, align: "right" },
    { text: `PAGADO: ${money.format(sale.paid)}`, align: "right" },
    { text: `SALDO: ${money.format(sale.balance)}`, bold: sale.balance > 0, align: "right" },
    { text: "--------------------------------" },
    { text: "PAGOS", bold: true },
  );
  sale.payments.forEach((payment) => lines.push({
    text: `${payment.methodCode} ${payment.methodLabel}: ${money.format(payment.amount)}${payment.reference ? ` Ref. ${payment.reference}` : ""}`,
  }));
  if (sale.layawayExpiresAt) {
    lines.push({ text: "--------------------------------" });
    lines.push({ text: `Apartado vigente hasta: ${new Intl.DateTimeFormat("es-MX", { dateStyle: "long" }).format(new Date(sale.layawayExpiresAt))}`, bold: true });
  }
  lines.push({ text: "--------------------------------" }, { text: `Atendio: ${sale.createdByName}` }, { text: "Gracias por su compra", bold: true, align: "center" });

  const document = await PDFDocument.create();
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const width = 226.77;
  const lineHeight = 12;
  const wrapped = lines.flatMap((line) => {
    const words = pdfText(line.text).split(/\s+/);
    const rows: string[] = [];
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > 35 && current) { rows.push(current); current = word; }
      else current = candidate;
    }
    if (current) rows.push(current);
    return rows.map((text) => ({ ...line, text }));
  });
  const height = Math.max(320, 44 + wrapped.length * lineHeight);
  const page = document.addPage([width, height]);
  let y = height - 24;
  wrapped.forEach((line) => {
    const font = line.bold ? bold : regular;
    const size = line.bold ? 9 : 8;
    const text = line.text;
    const measured = font.widthOfTextAtSize(text, size);
    const x = line.align === "center" ? Math.max(10, (width - measured) / 2)
      : line.align === "right" ? Math.max(10, width - measured - 12) : 12;
    page.drawText(text, { x, y, size, font, color: rgb(0.03, 0.12, 0.2) });
    y -= lineHeight;
  });
  return document.save();
}

async function openTicket(sale: CounterSale, download: boolean): Promise<void> {
  const bytes = await buildCounterSaleTicketPdf(sale);
  if (download) downloadPdf(bytes, `${sale.folio}.pdf`);
  else await printReceiptPdf(bytes, `${sale.folio}.pdf`);
}

export function CounterSalesOperations({ session, onClose }: { session: SessionIdentity; onClose: () => void }) {
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [operationType, setOperationType] = useState<CounterSaleOperation>("LIQUIDATED");
  const [customer, setCustomer] = useState<CustomerDraft>(emptyCustomer);
  const [paymentMethod, setPaymentMethod] = useState<StandardPaymentMethod>("01");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [message, setMessage] = useState("");
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSale, setSavedSale] = useState<CounterSale | null>(null);
  const [operationKey, setOperationKey] = useState(() => crypto.randomUUID());
  const [invoice, setInvoice] = useState<FiscalDraft>(emptyFiscalDraft);

  const totals = useMemo(() => {
    const total = roundMoney(cart.reduce((sum, item) => sum + item.publicPrice * item.quantity, 0));
    const subtotal = roundMoney(total / 1.16);
    const vat = roundMoney(total - subtotal);
    const paid = roundMoney(payments.reduce((sum, payment) => sum + payment.amount, 0));
    return {
      total, subtotal, vat, paid, balance: roundMoney(Math.max(0, total - paid)),
      minimum: operationType === "LAYAWAY_50" ? roundMoney(total * 0.5) : total,
    };
  }, [cart, payments, operationType]);

  async function addProduct(event: FormEvent) {
    event.preventDefault();
    const cleanSku = sku.replace(/\D/g, "");
    const requested = Math.floor(Number(quantity));
    if (cleanSku.length !== 6 || requested < 1 || requested > 999) {
      setMessage("Captura un SKU de seis dígitos y una cantidad válida."); return;
    }
    setLoadingProduct(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/counter/products/${cleanSku}`, {
        credentials: "include", headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const product = counterSaleProductResponseSchema.parse(await response.json()).data;
      const existing = cart.find((item) => item.sku === product.sku);
      if (existing && existing.quantity + requested > product.availableQuantity) throw new Error("Existencia insuficiente.");
      if (!existing && requested > product.availableQuantity) throw new Error("Existencia insuficiente.");
      setCart(existing
        ? cart.map((item) => item.sku === product.sku ? { ...product, quantity: item.quantity + requested } : item)
        : [...cart, { ...product, quantity: requested }]);
      setSku(""); setQuantity("1");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible consultar el producto.");
    } finally { setLoadingProduct(false); }
  }

  function updateQuantity(productSku: string, next: number) {
    setCart(cart.map((item) => item.sku === productSku
      ? { ...item, quantity: Math.max(1, Math.min(item.availableQuantity, Math.floor(next || 1))) }
      : item));
  }

  function addPayment() {
    const amount = roundMoney(Number(paymentAmount));
    if (!amount || amount <= 0) { setMessage("Captura una forma de pago y un importe mayor a cero."); return; }
    if (paymentMethod !== "01" && !paymentReference.trim()) { setMessage("La referencia es obligatoria cuando el pago no es en efectivo."); return; }
    if (amount > totals.balance + 0.009) { setMessage("El pago no puede superar el saldo pendiente."); return; }
    setPayments([...payments, { methodCode: paymentMethod, reference: paymentReference.trim(), amount }]);
    setPaymentReference(""); setPaymentAmount(""); setMessage("");
  }

  function resetCapture() {
    setCart([]); setPayments([]); setOperationType("LIQUIDATED"); setCustomer(emptyCustomer);
    setPaymentMethod("01"); setPaymentReference(""); setPaymentAmount("");
    setOperationKey(crypto.randomUUID());
    setInvoice(emptyFiscalDraft);
  }

  async function saveSale() {
    if (!cart.length) { setMessage("Agrega al menos un producto."); return; }
    if (!payments.length) { setMessage("Agrega al menos una forma de pago."); return; }
    if (operationType === "LIQUIDATED" && Math.abs(totals.paid - totals.total) > 0.009) {
      setMessage("Para una venta liquidada, los pagos deben cubrir el total completo."); return;
    }
    if (operationType === "LAYAWAY_50") {
      if (!customer.names || !customer.paternalSurname || !customer.maternalSurname || !customer.birthDate) {
        setMessage("Captura nombre, apellidos y fecha de nacimiento del cliente."); return;
      }
      if (!/^\d{10}$/.test(customer.phone)) { setMessage("El teléfono debe tener exactamente 10 dígitos."); return; }
      if (!customer.privacyAccepted) { setMessage("El cliente debe aceptar el aviso de privacidad."); return; }
      if (totals.paid + 0.009 < totals.minimum) { setMessage(`El apartado mínimo es ${money.format(totals.minimum)} (50% del total).`); return; }
      if (totals.paid >= totals.total - 0.009) { setMessage("Si el pago cubre el total, selecciona Venta liquidada."); return; }
    }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/counter`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json", "Idempotency-Key": operationKey },
        body: JSON.stringify({
          operationType,
          items: cart.map((item) => ({ sku: item.sku, quantity: item.quantity })),
          payments: payments.map((payment) => ({ ...payment, reference: payment.reference || null })),
          customer: operationType === "LAYAWAY_50" ? {
            names: customer.names, paternalSurname: customer.paternalSurname,
            maternalSurname: customer.maternalSurname, birthDate: customer.birthDate,
            phone: customer.phone, email: customer.email.trim() || null, privacyAccepted: true,
          } : null,
          invoice: invoicePayload(invoice),
        }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const sale = counterSaleResponseSchema.parse(await response.json()).data;
      setSavedSale(sale);
      const notices: string[] = [`${sale.folio} registrada como ${sale.status === "PAID" ? "pagada" : "apartado"}.`];
      if (payments.some((payment) => payment.methodCode === "01")) {
        const drawer = await openAuditedCashDrawer({
          openingType: "COUNTER_SALE_CASH",
          operationReference: sale.folio,
          paymentSequence: null,
          reason: "Cobro en efectivo de venta de mostrador",
        });
        notices.push(drawer.message);
      }
      try {
        const printResult = await printReceiptPdf(await buildCounterSaleTicketPdf(sale), `${sale.folio}.pdf`);
        notices.push(printResult === "LOCAL" ? "Ticket impreso." : "El ticket se abrió en el navegador.");
      } catch {
        notices.push("La operación quedó guardada, pero el ticket no pudo imprimirse automáticamente.");
      }
      setMessage(notices.join(" "));
      resetCapture();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible guardar la venta.");
    } finally { setSaving(false); }
  }

  const branch = session.branches.find((item) => item.id === session.branchId);
  return (
    <section className="admin-shell sales-shell counter-sales-shell" aria-labelledby="counter-title">
      <div className="section-heading"><div><p className="eyebrow">Etapa 5 de 10 · Operación rápida</p><h1 id="counter-title">Venta de mostrador</h1><p>Accesorios, Solar, Solar Kids y Solar Graduable. Folio, pago e inventario quedan protegidos en una sola operación.</p></div><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div>
      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

      <form className="sales-card" onSubmit={addProduct}>
        <h2>Agregar productos</h2>
        <div className="counter-product-entry"><label>SKU<input disabled={loadingProduct || saving} inputMode="numeric" maxLength={6} onChange={(event) => setSku(event.target.value.replace(/\D/g, ""))} placeholder="000000" required value={sku} /></label><label>Cantidad<input disabled={loadingProduct || saving} max="999" min="1" onChange={(event) => setQuantity(event.target.value)} required type="number" value={quantity} /></label><button className="primary-button" disabled={loadingProduct || saving}>{loadingProduct ? "Buscando…" : "Buscar y agregar"}</button></div>
        <div className="counter-table"><div className="counter-table-head"><span>SKU</span><span>CatP</span><span>Descripción</span><span>Cantidad</span><span>Precio c/IVA</span><span>Importe</span><span /></div>{cart.map((item) => <div className="counter-table-row" key={item.sku}><strong>{item.sku}</strong><span>{item.categoryLabel}</span><div><strong>{item.description}</strong><small>{item.brand} · {item.color} · {item.availableQuantity} disponibles</small></div><input aria-label={`Cantidad SKU ${item.sku}`} max={item.availableQuantity} min="1" onChange={(event) => updateQuantity(item.sku, Number(event.target.value))} type="number" value={item.quantity} /><span>{money.format(item.publicPrice)}</span><strong>{money.format(item.publicPrice * item.quantity)}</strong><button className="text-button" onClick={() => setCart(cart.filter((product) => product.sku !== item.sku))} type="button">Quitar</button></div>)}{cart.length === 0 && <p className="empty-state">Aún no hay productos.</p>}</div>
      </form>

      <section className="sales-card"><h2>Operación y promoción</h2><div className="field-grid"><label>Tipo de operación<select disabled={saving} onChange={(event) => setOperationType(event.target.value as CounterSaleOperation)} value={operationType}><option value="LIQUIDATED">Venta liquidada</option><option value="LAYAWAY_50">Apartado (mínimo 50%)</option></select></label><label>Promoción vigente<select disabled><option>Sin promoción</option></select></label></div>{operationType === "LAYAWAY_50" && <p className="lens-notice warning">Para apartar, captura al cliente y registra pagos que cubran por lo menos el 50% del total. Las piezas quedarán reservadas durante 30 días.</p>}</section>

      {operationType === "LAYAWAY_50" && <section className="sales-card"><h2>Datos del cliente para apartado</h2><p>Se utiliza el mismo registro de cliente de la venta graduada.</p><div className="field-grid"><label>Nombre(s)<input disabled={saving} onChange={(event) => setCustomer({ ...customer, names: event.target.value })} required value={customer.names} /></label><label>Apellido paterno<input disabled={saving} onChange={(event) => setCustomer({ ...customer, paternalSurname: event.target.value })} required value={customer.paternalSurname} /></label><label>Apellido materno<input disabled={saving} onChange={(event) => setCustomer({ ...customer, maternalSurname: event.target.value })} required value={customer.maternalSurname} /></label><label>Fecha de nacimiento<input disabled={saving} max={new Date().toISOString().slice(0, 10)} onChange={(event) => setCustomer({ ...customer, birthDate: event.target.value })} required type="date" value={customer.birthDate} /></label><label>Teléfono a 10 dígitos<input disabled={saving} inputMode="numeric" maxLength={10} onChange={(event) => setCustomer({ ...customer, phone: event.target.value.replace(/\D/g, "") })} required value={customer.phone} /></label><label>Correo opcional<input disabled={saving} onChange={(event) => setCustomer({ ...customer, email: event.target.value })} type="email" value={customer.email} /></label></div><label className="check-line"><input checked={customer.privacyAccepted} disabled={saving} onChange={(event) => setCustomer({ ...customer, privacyAccepted: event.target.checked })} type="checkbox" />El cliente acepta el aviso de privacidad y el tratamiento de sus datos.</label></section>}

      <FiscalRequestFields disabled={saving} onChange={setInvoice} value={invoice} />

      <section className="sales-card"><h2>Pagos</h2><p>Puedes registrar varias formas de pago. La de mayor importe se utilizará como forma dominante para facturación.</p><div className="counter-payment-entry"><label>Forma de pago<select disabled={saving} onChange={(event) => setPaymentMethod(event.target.value as StandardPaymentMethod)} value={paymentMethod}>{paymentMethods.map((method) => <option key={method.code} value={method.code}>{method.code} - {method.label}</option>)}</select></label><label>Referencia<input disabled={saving || paymentMethod === "01"} onChange={(event) => setPaymentReference(event.target.value)} placeholder={paymentMethod === "01" ? "No aplica" : "Obligatoria"} value={paymentReference} /></label><label>Importe<input disabled={saving || totals.balance <= 0} min="0.01" onChange={(event) => setPaymentAmount(event.target.value)} placeholder={totals.balance > 0 ? totals.balance.toFixed(2) : "0.00"} step="0.01" type="number" value={paymentAmount} /></label><button className="primary-button" disabled={saving || totals.balance <= 0} onClick={addPayment} type="button">Agregar pago</button></div><div className="counter-payments">{payments.map((payment, index) => <div key={`${payment.methodCode}-${index}`}><span>{payment.methodCode} - {paymentMethods.find((method) => method.code === payment.methodCode)?.label}</span><span>{payment.reference || "Sin referencia"}</span><strong>{money.format(payment.amount)}</strong><button className="text-button" onClick={() => setPayments(payments.filter((_, paymentIndex) => paymentIndex !== index))}>Quitar</button></div>)}{payments.length === 0 && <p className="empty-state">Aún no hay pagos agregados.</p>}</div><div className="counter-totals"><div><span>Total de lista (IVA incluido)</span><strong>{money.format(totals.total)}</strong></div><div><span>Descuento por promoción</span><strong>{money.format(0)}</strong></div><div><span>Subtotal sin IVA</span><strong>{money.format(totals.subtotal)}</strong></div><div><span>IVA incluido (16%)</span><strong>{money.format(totals.vat)}</strong></div><div className="counter-grand"><span>TOTAL</span><strong>{money.format(totals.total)}</strong></div><div><span>Importe mínimo a pagar</span><strong>{money.format(totals.minimum)}</strong></div><div><span>Total recibido</span><strong>{money.format(totals.paid)}</strong></div><div><span>Saldo pendiente</span><strong>{money.format(totals.balance)}</strong></div></div><div className="step-actions"><span className="branch-chip">Sucursal: {branch?.code} · {branch?.name}</span><button className="primary-button" disabled={saving || cart.length === 0} onClick={saveSale} type="button">{saving ? "Guardando operación…" : "Guardar operación y generar ticket"}</button></div></section>

      {savedSale && <section className="quote-success counter-success"><span>Operación registrada</span><strong>{savedSale.folio}</strong><p>{savedSale.status === "PAID" ? "Venta pagada e inventario descontado." : `Apartado por ${money.format(savedSale.paid)}; saldo ${money.format(savedSale.balance)}.`}</p><div><button className="secondary-button" onClick={() => openTicket(savedSale, true)}>Descargar PDF</button><button className="primary-button" onClick={() => openTicket(savedSale, false)}>Imprimir ticket 80 mm</button></div></section>}
    </section>
  );
}
